"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
Object.defineProperty(exports, "runAction", {
    enumerable: true,
    get: function() {
        return runAction;
    }
});
const _createTargetGraph = require("./createTargetGraph.js");
const _filterArgsForTasks = require("./filterArgsForTasks.js");
const _filterPipelineDefinitions = require("./filterPipelineDefinitions.js");
const _config = require("@lage-run/config");
const _workspacetools = require("workspace-tools");
const _initializeReporters = require("../initializeReporters.js");
const _scheduler = require("@lage-run/scheduler");
const _logger = /*#__PURE__*/ _interop_require_default(require("@lage-run/logger"));
const _errors = require("../../types/errors.js");
const _createCacheProvider = require("../../cache/createCacheProvider.js");
const _runnerPickerOptions = require("../../runnerPickerOptions.js");
const _optimizeTargetGraph = require("../../optimizeTargetGraph.js");
function _interop_require_default(obj) {
    return obj && obj.__esModule ? obj : {
        default: obj
    };
}
async function runAction(options, command) {
    const cwd = process.cwd();
    const config = await (0, _config.getConfig)(cwd);
    // Merged options
    const concurrency = (0, _config.getConcurrency)(options.concurrency, config.concurrency);
    const allowNoTargetRuns = options.allowNoTargetRuns || config.allowNoTargetRuns;
    // Configure logger
    const logger = (0, _logger.default)();
    const reporters = await (0, _initializeReporters.initializeReporters)(logger, {
        ...options,
        concurrency
    }, config.reporters);
    // Build Target Graph
    const root = (0, _workspacetools.getWorkspaceRoot)(process.cwd());
    const packageInfos = (0, _workspacetools.getPackageInfos)(root);
    const { tasks, taskArgs } = (0, _filterArgsForTasks.filterArgsForTasks)(command.args);
    const targetGraph = await (0, _createTargetGraph.createTargetGraph)({
        logger,
        root,
        dependencies: options.dependencies,
        dependents: options.dependents && !options.to,
        ignore: options.ignore.concat(config.ignore),
        pipeline: config.pipeline,
        repoWideChanges: config.repoWideChanges,
        scope: (options.scope ?? []).concat(options.to ?? []),
        since: options.since,
        outputs: config.cacheOptions.outputGlob,
        tasks,
        packageInfos,
        priorities: config.priorities,
        enableTargetConfigMerging: config.enableTargetConfigMerging
    });
    validateTargetGraph(targetGraph, allowNoTargetRuns);
    logger.verbose(`Running with ${concurrency} workers`);
    const filteredPipeline = (0, _filterPipelineDefinitions.filterPipelineDefinitions)(targetGraph.targets.values(), config.pipeline);
    const maxWorkersPerTaskMap = (0, _config.getMaxWorkersPerTaskFromOptions)(options.maxWorkersPerTask);
    const { hasher } = await (0, _createCacheProvider.createCache)({
        root,
        logger,
        cacheOptions: config.cacheOptions,
        cliArgs: taskArgs,
        skipLocalCache: options.skipLocalCache
    });
    const scheduler = new _scheduler.SimpleScheduler({
        logger,
        concurrency,
        continueOnError: options.continue,
        shouldCache: options.cache,
        shouldResetCache: options.resetCache,
        workerData: {
            root,
            taskArgs,
            skipLocalCache: options.skipLocalCache,
            cacheOptions: config.cacheOptions,
            runners: {
                ...(0, _runnerPickerOptions.runnerPickerOptions)(options.nodeArg, config.npmClient, taskArgs),
                ...config.runners
            }
        },
        maxWorkersPerTask: new Map([
            ...(0, _config.getMaxWorkersPerTask)(filteredPipeline, concurrency),
            ...maxWorkersPerTaskMap
        ]),
        hasher,
        workerIdleMemoryLimit: config.workerIdleMemoryLimit
    });
    const optimizedTargets = await (0, _optimizeTargetGraph.optimizeTargetGraph)(targetGraph, scheduler.runnerPicker, false);
    const optimizedGraph = {
        targets: new Map(optimizedTargets.map((target)=>[
                target.id,
                target
            ]))
    };
    const summary = await scheduler.run(root, optimizedGraph);
    await scheduler.cleanup();
    displaySummaryAndExit(summary, logger.reporters);
    for (const reporter of reporters){
        await reporter.cleanup?.();
    }
}
function displaySummaryAndExit(summary, reporters) {
    if (summary.results !== "success") {
        process.exitCode = 1;
    }
    for (const reporter of reporters){
        reporter.summarize(summary);
    }
}
function validateTargetGraph(targetGraph, allowNoTargetRuns) {
    const visibleTargets = Array.from(targetGraph.targets.values()).filter((target)=>!target.hidden);
    if (visibleTargets.length === 0 && !allowNoTargetRuns) {
        throw _errors.NoTargetFoundError;
    }
}
